import { Checkmark, Subtract } from '@carbon/icons-react';
import { forwardRef, useEffect, useId, useRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '@/shared/lib/cx';
import { mergeRefs } from '@/shared/lib/hooks';
import s from './Choice.module.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label: ReactNode;
  description?: ReactNode;
  /** Частичный выбор (например, «выбраны не все строки»). */
  indeterminate?: boolean;
  invalid?: boolean;
  /** Подпись только для экранных дикторов (чекбокс строки таблицы). */
  hideLabel?: boolean;
}

/** Чекбокс на нативном input: клавиатура, формы и экранные дикторы работают без доработок. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, indeterminate = false, invalid = false, hideLabel = false, className, id, disabled, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? `checkbox-${autoId}`;
  const labelId = `${inputId}-label`;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const innerRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label className={cx(s.choice, className)} htmlFor={inputId} data-disabled={disabled ? 'true' : undefined} data-invalid={invalid ? 'true' : undefined}>
      <input
        ref={mergeRefs(ref, innerRef)}
        id={inputId}
        type="checkbox"
        className={s.input}
        disabled={disabled}
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      <span className={s.box} aria-hidden="true">
        <Checkmark size={16} className={s.check} />
        <Subtract size={16} className={s.dash} />
      </span>
      <span className={cx(s.text, hideLabel && 'visually-hidden')}>
        <span id={labelId}>{label}</span>
        {description ? (
          <span id={descriptionId} className={s.description}>
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
});
